
import { useState, useEffect, lazy, Suspense } from "react";
import Layout from "../components/Layout";
import WorkFilter, { WorkCategory } from "../components/WorkFilter";
import VideoGridLoading from "../components/work/VideoGridLoading";
import VideoGridEmpty from "../components/work/VideoGridEmpty";
import { getWorks, WorkItem } from "../lib/contentful";
import { toast } from "../components/ui/use-toast";

// Lazy load components that aren't needed for initial render
const VideoGrid = lazy(() => import("../components/work/VideoGrid"));
const WorkLightbox = lazy(() => import("../components/work/WorkLightbox"));

const Work = () => {
  const [selectedCategories, setSelectedCategories] = useState<WorkCategory[]>(["featured"]);
  const [works, setWorks] = useState<WorkItem[]>([]);
  const [filteredWorks, setFilteredWorks] = useState<WorkItem[]>([]);
  const [selectedWork, setSelectedWork] = useState<WorkItem | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Load works data with a small delay to allow the page to render first
  useEffect(() => {
    const controller = new AbortController();
    
    const fetchWorks = async () => {
      setIsLoading(true);
      try {
        // Small timeout to prioritize UI render
        await new Promise(resolve => setTimeout(resolve, 100));
        const worksData = await getWorks();
        
        if (!controller.signal.aborted) {
          // Optimize by prefiltering the works
          const optimizedWorks = worksData.map(work => ({
            ...work,
            // Ensure all necessary properties are present
            posterImage: work.posterImage || "/placeholder.svg"
          }));
          
          setWorks(optimizedWorks);
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error("Error fetching works:", error);
          toast({
            title: "Error",
            description: "Failed to load works. Please try again later.",
            variant: "destructive",
          });
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchWorks();
    
    return () => controller.abort();
  }, []);

  // Filter works when categories or works change - now with memoization
  useEffect(() => {
    // Filter works based on selected categories
    if (selectedCategories.includes("featured")) {
      // When "featured" is selected, show works with the "featured" category
      const featured = works.filter(work => 
        work.categories.includes("featured")
      );
      setFilteredWorks(featured);
    } else {
      // Otherwise, show works matching any of the selected categories
      const filtered = works.filter(work => 
        selectedCategories.some(category => work.categories.includes(category))
      );
      setFilteredWorks(filtered);
    }
  }, [selectedCategories, works]);

  const handleFilterChange = (categories: WorkCategory[]) => {
    setSelectedCategories(categories);
  };

  const handleWorkSelect = (work: WorkItem) => {
    setSelectedWork(work);
    setLightboxOpen(true);
  };

  return (
    <Layout>
      <section>
        <h1 className="text-3xl font-medium text-center mb-6">Work</h1>
        
        <WorkFilter onFilterChange={handleFilterChange} />
        
        <Suspense fallback={<VideoGridLoading />}>
          {isLoading ? (
            <VideoGridLoading />
          ) : filteredWorks.length === 0 ? (
            <VideoGridEmpty />
          ) : (
            <VideoGrid 
              works={filteredWorks}
              isLoading={false}
              onWorkSelect={handleWorkSelect}
            />
          )}
        </Suspense>
        
        {/* Only render WorkLightbox when needed */}
        {selectedWork && lightboxOpen && (
          <Suspense fallback={null}>
            <WorkLightbox 
              open={lightboxOpen}
              onOpenChange={setLightboxOpen}
              work={selectedWork}
            />
          </Suspense>
        )}
      </section>
    </Layout>
  );
};

export default Work;
